import { money } from '../format';
import type { HealthOut, Identity, TeamSummary } from '../types';

interface Props {
  health: HealthOut | null;
  me: Identity | null;
  teams: TeamSummary[] | null;
  /** Counted from the rendered list, not summed from teams: unmanaged keys have no
   * team, so a teams-derived total silently disagrees with the table below it. */
  keyCount: number | null;
  /** From the Entra token. Used only when /api/me failed, so a degraded backend still
   * shows who is signed in — we are demonstrably authenticated at that point. Null in
   * mock mode, where there is no token to read a name from. */
  accountLabel: string | null;
}

export function StatusBar({ health, me, teams, keyCount, accountLabel }: Props) {
  const teamCount = teams?.length ?? null;
  // Sum of member keys' limits — the app-side team cap while workspace budgets are
  // plan-gated. Only meaningful for teams that actually have a cap set.
  const capped = teams?.filter((t) => t.total_key_limit_usd !== null) ?? [];
  const totalCap = capped.length
    ? capped.reduce((n, t) => n + (t.total_key_limit_usd ?? 0), 0)
    : null;

  return (
    <div className="statusbar">
      <Tile label="Teams" value={teamCount === null ? '-' : String(teamCount)} />
      <Tile
        label="Keys visible to you"
        value={keyCount === null ? '-' : String(keyCount)}
        note="includes unclaimed keys"
      />
      {/* teams === null means the request failed: say "unknown", never "none set" —
          claiming no limits exist when we could not read them is the worse error. */}
      <Tile
        label="Allocated key limits"
        value={teams === null ? 'unknown' : money(totalCap, 'none set')}
        note={
          teams === null
            ? 'could not load teams'
            : totalCap === null
              ? 'no team has limits set'
              : `sum of key limits across ${capped.length} of ${teamCount} teams`
        }
      />
      <Tile
        label="Signed in as"
        value={me?.display_name ?? accountLabel ?? 'unknown'}
        note={
          me
            ? `${me.is_platform_admin ? 'platform admin' : roleSummary(me)} - auth ${me.auth_mode}`
            : accountLabel
              ? // Signed in, but roles are unknown: /api/me is where they come from.
                'signed in - could not load your roles'
              : undefined
        }
      />
      <Tile
        label="Service"
        value={health ? health.status : 'unknown'}
        note={health ? `v${health.version} - ${databaseNote(health)}` : undefined}
        severity={health && health.status === 'ok' ? 'ok' : 'warning'}
      />
    </div>
  );
}

function databaseNote(health: HealthOut): string {
  if (health.database !== 'ok') return 'database unreachable';
  // Connected but unmigrated: reporting only "database ok" here would describe a
  // deployment whose every data endpoint 500s as healthy.
  return health.schema_ready ? 'database ok' : 'database ok, schema not migrated';
}

function roleSummary(me: Identity): string {
  if (me.teams.length === 0) return 'no team membership';
  const leads = me.teams.filter((t) => t.role === 'lead').length;
  return leads > 0 ? `lead of ${leads} of ${me.teams.length} teams` : `member of ${me.teams.length} teams`;
}

function Tile({
  label,
  value,
  note,
  severity,
}: {
  label: string;
  value: string;
  note?: string | undefined;
  severity?: 'ok' | 'warning';
}) {
  return (
    <div className={`tile${severity ? ` tile-${severity}` : ''}`}>
      <div className="tile-label">{label}</div>
      <div className="tile-value">{value}</div>
      <div className="tile-note">{note ?? ' '}</div>
    </div>
  );
}
